import React, { useState, useEffect, useCallback } from "react";
import { Shield, Users, LayoutGrid, Instagram, Link2, Plus, Trash2, Pencil, KeyRound, ChevronDown, ChevronUp, RefreshCw, LogOut, X } from "lucide-react";
import api, { apiError } from "../api";
import { T, primaryBtn, inputStyle, PLAN_LABELS } from "../theme";

const NICHES = ["Ristorante / Bar", "Negozio locale", "Artigiano", "Professionista", "Palestra / Wellness", "Bellezza / Estetica", "Altro"];
const GOALS = ["Più follower", "Più contatti o richieste", "Più vendite dirette", "Più autorevolezza"];

const cardStyle = { background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16 };
const smallBtn = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 6, padding: "5px 9px", cursor: "pointer" };
const selStyle = { ...inputStyle, width: "auto", padding: "5px 8px", fontSize: 12 };

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" }); } catch { return "-"; }
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} style={{ ...cardStyle, flex: "1 1 130px", minWidth: 130 }}>
      <div style={{ fontSize: 12, color: T.textDim, marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: T.fontHead, fontSize: 22 }}>{value}</div>
    </div>
  );
}

function ProfileForm({ initial, onSave, onCancel, saveLabel }) {
  const [name, setName] = useState(initial?.name || "");
  const [niche, setNiche] = useState(initial?.niche || NICHES[0]);
  const [goal, setGoal] = useState(initial?.goal || GOALS[0]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  async function save() {
    if (!name.trim()) { setErr("Inserisci un nome."); return; }
    setBusy(true); setErr("");
    try { await onSave({ name: name.trim(), niche, goal }); }
    catch (e) { setErr(apiError(e)); }
    finally { setBusy(false); }
  }
  return (
    <div data-testid="admin-profile-form" style={{ background: T.surfaceAlt, border: `1px solid ${T.line}`, borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
      <input data-testid="admin-profile-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome attività / profilo" style={inputStyle} />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <select data-testid="admin-profile-niche-select" value={niche} onChange={(e) => setNiche(e.target.value)} style={selStyle}>
          {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <select data-testid="admin-profile-goal-select" value={goal} onChange={(e) => setGoal(e.target.value)} style={selStyle}>
          {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      {err && <div style={{ fontSize: 12, color: T.rose }}>{err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button data-testid="admin-profile-save-btn" onClick={save} disabled={busy} style={{ ...primaryBtn, padding: "7px 14px", fontSize: 12 }}>{busy ? "Un momento…" : saveLabel}</button>
        <button data-testid="admin-profile-cancel-btn" onClick={onCancel} style={smallBtn}>Annulla</button>
      </div>
    </div>
  );
}

function ConfirmDelete({ title, body, onConfirm, onCancel, requireText }) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const blocked = requireText && typed !== requireText;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(30,30,51,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 120 }}>
      <div data-testid="admin-confirm-dialog" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 12, padding: 24, width: 380, maxWidth: "90vw" }}>
        <div style={{ fontFamily: T.fontHead, fontSize: 16, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.5, marginBottom: 14 }}>{body}</div>
        {requireText && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: T.textDim, marginBottom: 6 }}>Per confermare, scrivi <b style={{ color: T.rose }}>{requireText}</b>:</div>
            <input data-testid="admin-confirm-text-input" value={typed} onChange={(e) => setTyped(e.target.value.toUpperCase())} placeholder={requireText} style={inputStyle} />
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button data-testid="admin-confirm-cancel-btn" onClick={onCancel} style={smallBtn}>Annulla</button>
          <button data-testid="admin-confirm-delete-btn" onClick={async () => { setBusy(true); await onConfirm(); }} disabled={busy || blocked}
            style={{ ...primaryBtn, background: T.rose, padding: "7px 14px", fontSize: 12, opacity: busy || blocked ? 0.5 : 1 }}>{busy ? "Elimino…" : "Elimina definitivamente"}</button>
        </div>
      </div>
    </div>
  );
}

function UserRow({ u, currentUser, onChanged, notify }) {
  const [open, setOpen] = useState(false);
  const [addingProfile, setAddingProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState(null);
  const isSelf = u.id === currentUser.id;

  async function patchUser(patch, okMsg) {
    try { await api.patch(`/admin/users/${u.id}`, patch); notify(okMsg); onChanged(); }
    catch (e) { notify(apiError(e), true); }
  }

  return (
    <div data-testid={`admin-user-row-${u.email}`} style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", flexWrap: "wrap" }}>
        <button data-testid={`admin-user-toggle-${u.email}`} onClick={() => setOpen((o) => !o)} style={{ background: "transparent", border: "none", color: T.textDim, cursor: "pointer", padding: 2 }}>
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
        <div style={{ flex: "1 1 180px", minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, wordBreak: "break-all" }}>{u.email}
            {u.role === "master" && <span style={{ marginLeft: 8, fontSize: 10, background: `${T.amber}22`, color: T.amber, borderRadius: 4, padding: "2px 6px", verticalAlign: "middle", whiteSpace: "nowrap", display: "inline-block" }}>MASTER</span>}
            {isSelf && <span style={{ marginLeft: 6, fontSize: 10, color: T.textDim }}>(tu)</span>}
          </div>
          <div style={{ fontSize: 11, color: T.textDim }}>Registrato: {fmtDate(u.created_at)} · {u.profiles.length} {u.profiles.length === 1 ? "profilo" : "profili"}</div>
        </div>
        <select data-testid={`admin-user-plan-${u.email}`} value={u.plan} onChange={(e) => patchUser({ plan: e.target.value }, "Piano aggiornato.")} style={selStyle}>
          {Object.keys(PLAN_LABELS).map((p) => <option key={p} value={p}>{PLAN_LABELS[p]}</option>)}
        </select>
        <select data-testid={`admin-user-role-${u.email}`} value={u.role} disabled={isSelf} onChange={(e) => patchUser({ role: e.target.value }, "Ruolo aggiornato.")} style={{ ...selStyle, opacity: isSelf ? 0.5 : 1 }}>
          <option value="user">Utente</option>
          <option value="master">Master</option>
        </select>
        <button data-testid={`admin-user-resetpw-${u.email}`} onClick={() => { setResetting((r) => !r); setNewPw(""); }} title="Nuova password" style={smallBtn}><KeyRound size={12} /> Password</button>
        {!isSelf && (
          <button data-testid={`admin-user-delete-${u.email}`} onClick={() => setConfirm({
            title: "Eliminare questo utente?",
            requireText: "ELIMINA",
            body: `${u.email} verrà eliminato insieme a ${u.profiles.length} profili e tutti i contenuti. Questa azione non si può annullare.`,
            run: async () => {
              try { await api.delete(`/admin/users/${u.id}`); notify("Utente eliminato."); onChanged(); }
              catch (e) { notify(apiError(e), true); }
              setConfirm(null);
            },
          })} title="Elimina utente" style={{ ...smallBtn, color: T.rose, borderColor: `${T.rose}66` }}><Trash2 size={12} /></button>
        )}
      </div>

      {resetting && (
        <div style={{ padding: "0 14px 12px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input data-testid={`admin-user-newpw-input-${u.email}`} type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nuova password (min 6 caratteri)" style={{ ...inputStyle, maxWidth: 260 }} />
          <button data-testid={`admin-user-newpw-save-${u.email}`} onClick={async () => {
            if (newPw.length < 6) { notify("La password deve avere almeno 6 caratteri.", true); return; }
            await patchUser({ new_password: newPw }, "Password aggiornata.");
            setResetting(false);
          }} style={{ ...primaryBtn, padding: "7px 14px", fontSize: 12 }}>Salva</button>
          <button onClick={() => setResetting(false)} style={smallBtn}><X size={12} /></button>
        </div>
      )}

      {open && (
        <div style={{ borderTop: `1px solid ${T.line}`, padding: "12px 14px", background: T.bg }}>
          {u.profiles.length === 0 && <div style={{ fontSize: 12, color: T.textDim, marginBottom: 8 }}>Nessun profilo.</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {u.profiles.map((p) => (
              <div key={p.id} data-testid={`admin-profile-row-${p.id}`} style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 8, padding: "10px 12px" }}>
                {editingProfile === p.id ? (
                  <ProfileForm initial={p} saveLabel="Salva modifiche"
                    onSave={async (form) => { await api.patch(`/admin/profiles/${p.id}`, form); notify("Profilo aggiornato."); setEditingProfile(null); onChanged(); }}
                    onCancel={() => setEditingProfile(null)} />
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 150px" }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: T.textDim }}>{p.niche} · {p.goal}</div>
                    </div>
                    <div style={{ display: "flex", gap: 14, fontSize: 11, color: T.textDim, flexWrap: "wrap" }}>
                      <span title="Follower"><b style={{ color: T.text }}>{(p.followers || 0).toLocaleString("it-IT")}</b> follower</span>
                      <span title="Contenuti in calendario"><b style={{ color: T.text }}>{p.entries}</b> contenuti</span>
                      <span title="Programmati"><b style={{ color: T.text }}>{p.scheduled}</b> programmati</span>
                      <span style={{ color: p.instagram_connected ? T.teal : T.textDim, display: "inline-flex", alignItems: "center", gap: 3 }}><Instagram size={11} /> {p.instagram_connected ? "collegato" : "no"}</span>
                      <span style={{ color: p.bio_published ? T.teal : T.textDim, display: "inline-flex", alignItems: "center", gap: 3 }}><Link2 size={11} /> {p.bio_published ? "bio online" : "bio offline"}</span>
                    </div>
                    <button data-testid={`admin-profile-edit-${p.id}`} onClick={() => setEditingProfile(p.id)} title="Modifica profilo" style={smallBtn}><Pencil size={11} /></button>
                    <button data-testid={`admin-profile-delete-${p.id}`} onClick={() => setConfirm({
                      title: "Eliminare questo profilo?",
                      body: `"${p.name}" verrà eliminato con calendario e dati di andamento. Questa azione non si può annullare.`,
                      run: async () => {
                        try { await api.delete(`/admin/profiles/${p.id}`); notify("Profilo eliminato."); onChanged(); }
                        catch (e) { notify(apiError(e), true); }
                        setConfirm(null);
                      },
                    })} title="Elimina profilo" style={{ ...smallBtn, color: T.rose, borderColor: `${T.rose}66` }}><Trash2 size={11} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {addingProfile ? (
            <ProfileForm saveLabel="Crea profilo"
              onSave={async (form) => { await api.post(`/admin/users/${u.id}/profiles`, form); notify("Profilo creato."); setAddingProfile(false); onChanged(); }}
              onCancel={() => setAddingProfile(false)} />
          ) : (
            <button data-testid={`admin-add-profile-${u.email}`} onClick={() => setAddingProfile(true)} style={{ ...smallBtn, marginTop: 10 }}><Plus size={12} /> Aggiungi profilo</button>
          )}
        </div>
      )}

      {confirm && <ConfirmDelete title={confirm.title} body={confirm.body} requireText={confirm.requireText} onConfirm={confirm.run} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

export default function AdminPanel({ currentUser, onBack, standalone, onCreateOwnProfile, onLogout }) {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", plan: "free" });
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");

  const notify = useCallback((msg, isError) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    try {
      const [ov, us] = await Promise.all([api.get("/admin/overview"), api.get("/admin/users")]);
      setOverview(ov.data);
      setUsers(us.data.users);
    } catch (e) {
      notify(apiError(e), true);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  async function createUser() {
    if (!newUser.email || newUser.password.length < 6) { notify("Inserisci email e una password di almeno 6 caratteri.", true); return; }
    try {
      await api.post("/admin/users", newUser);
      notify("Utente creato.");
      setCreating(false);
      setNewUser({ email: "", password: "", plan: "free" });
      load();
    } catch (e) { notify(apiError(e), true); }
  }

  const filtered = search ? users.filter((u) => u.email.includes(search.toLowerCase()) || u.profiles.some((p) => p.name.toLowerCase().includes(search.toLowerCase()))) : users;
  const scheduled = overview?.entries_by_status?.programmato || 0;
  const approved = overview?.entries_by_status?.approvato || 0;

  return (
    <div data-testid="admin-panel">
      {toast && (
        <div data-testid="admin-toast" style={{ position: "fixed", top: 56, right: 20, background: T.surfaceAlt, border: `1px solid ${toast.isError ? T.rose : T.teal}`, color: T.text, padding: "10px 16px", borderRadius: 8, fontSize: 13, zIndex: 130 }}>
          {toast.msg}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Shield size={20} color={T.amber} />
          <div>
            <div style={{ fontFamily: T.fontHead, fontSize: 22 }}>Amministrazione</div>
            <div style={{ fontSize: 12, color: T.textDim }}>Tutti gli utenti e i profili di So.Help</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button data-testid="admin-refresh-btn" onClick={load} style={smallBtn}><RefreshCw size={12} /> Aggiorna</button>
          {standalone ? (
            <>
              <button data-testid="admin-create-own-profile-btn" onClick={onCreateOwnProfile} style={{ ...primaryBtn, padding: "7px 14px", fontSize: 12 }}>Crea il mio primo profilo</button>
              <button data-testid="admin-logout-btn" onClick={onLogout} style={smallBtn}><LogOut size={12} /> Esci</button>
            </>
          ) : (
            <button data-testid="admin-back-btn" onClick={onBack} style={smallBtn}>← Torna all'app</button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: T.textDim }}>Caricamento…</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
            <Stat testId="admin-stat-users" label="Utenti totali" value={overview?.users_total ?? "-"} />
            <Stat testId="admin-stat-new-users" label="Nuovi (30 giorni)" value={overview?.users_new_30d ?? "-"} />
            <Stat testId="admin-stat-profiles" label="Profili" value={overview?.profiles_total ?? "-"} />
            <Stat testId="admin-stat-entries" label="Contenuti" value={overview?.entries_total ?? "-"} />
            <Stat testId="admin-stat-scheduled" label="Programmati" value={scheduled} />
            <Stat testId="admin-stat-approved" label="Approvati" value={approved} />
            <Stat testId="admin-stat-instagram" label="Instagram collegati" value={overview?.instagram_connected ?? "-"} />
            <Stat testId="admin-stat-bio" label="Bio online" value={overview?.bio_published ?? "-"} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: T.fontHead, fontSize: 16 }}>
              <Users size={16} color={T.amber} /> Utenti ({users.length})
            </div>
            <input data-testid="admin-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per email o profilo…" style={{ ...inputStyle, maxWidth: 240, padding: "6px 10px", fontSize: 12 }} />
            <div style={{ flex: 1 }} />
            <button data-testid="admin-new-user-btn" onClick={() => setCreating((c) => !c)} style={{ ...primaryBtn, padding: "7px 14px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}><Plus size={13} /> Nuovo utente</button>
          </div>

          {creating && (
            <div data-testid="admin-new-user-form" style={{ ...cardStyle, marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input data-testid="admin-new-user-email" type="email" value={newUser.email} onChange={(e) => setNewUser((n) => ({ ...n, email: e.target.value }))} placeholder="email@esempio.it" style={{ ...inputStyle, maxWidth: 240 }} />
              <input data-testid="admin-new-user-password" type="text" value={newUser.password} onChange={(e) => setNewUser((n) => ({ ...n, password: e.target.value }))} placeholder="Password (min 6)" style={{ ...inputStyle, maxWidth: 200 }} />
              <select data-testid="admin-new-user-plan" value={newUser.plan} onChange={(e) => setNewUser((n) => ({ ...n, plan: e.target.value }))} style={selStyle}>
                {Object.keys(PLAN_LABELS).map((p) => <option key={p} value={p}>{PLAN_LABELS[p]}</option>)}
              </select>
              <button data-testid="admin-new-user-save" onClick={createUser} style={{ ...primaryBtn, padding: "7px 14px", fontSize: 12 }}>Crea</button>
              <button onClick={() => setCreating(false)} style={smallBtn}>Annulla</button>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map((u) => <UserRow key={u.id} u={u} currentUser={currentUser} onChanged={load} notify={notify} />)}
            {filtered.length === 0 && <div style={{ fontSize: 13, color: T.textDim }}>Nessun utente trovato.</div>}
          </div>

          <div style={{ marginTop: 16, fontSize: 11, color: T.textDim, display: "flex", alignItems: "center", gap: 6 }}>
            <LayoutGrid size={11} /> Piani: {overview && Object.entries(overview.plans).map(([p, n]) => `${PLAN_LABELS[p]}: ${n}`).join(" · ")}
          </div>
        </>
      )}
    </div>
  );
}
